/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useCallback, useEffect, useState } from "react";
import { Button } from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { useQuery } from "@tanstack/react-query";
import { Loader } from "../../../components/loader";
import { Query_Keys } from "../../../lib/queryKeys";
import { useNotificationContext } from "../../../contexts/notificationContext";
import { fetchDailyLogbookDetail } from "./api";
import { DrillInDialog } from "./drill_in_dialog";
import { DailyLogbookExportDialog } from "./export_dialog";
import { buildRestaurantExportUrl, exportFilename } from "./export";
import { useDailyLogbookExport } from "./use_daily_logbook_export";
import { DailyLogbookMetricPanel } from "./metric_panel";
import { DailyLogbookSections } from "./sections";
import { RevenueCenterRail } from "./revenue_center_rail";
import { RevenueCenterModal } from "./revenue_center_modal";
import { drillInExportButtonStyles } from "./styles";

const LOAD_ERROR_MESSAGE = "We could not load this restaurant's details.";
const NO_DETAILS_MESSAGE = "No details for this restaurant on this day.";

/**
 * The restaurant drill-in: its own figures and revenue-center list on the left, its
 * sections on the right. A revenue center opens in its own modal rather than replacing
 * anything here, so the restaurant view stays put underneath it.
 *
 * Fetched on open rather than prefetched per row — the endpoint's cost is constant in
 * revenue-center count, so one request when the modal opens is cheaper than a request
 * per restaurant on the page.
 */
export const DailyLogbookDetailsModal = ({
  open,
  restaurant,
  businessDate,
  businessDateLabel,
  onClose,
}) => {
  const showNotification = useNotificationContext();
  const [selectedCenter, setSelectedCenter] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const { exporting, runExport } = useDailyLogbookExport();

  const restaurantId = restaurant?.restaurant_id;

  const {
    data: detail,
    isFetching,
    error,
  } = useQuery({
    queryKey: Query_Keys.daily_logbook_detail({ restaurantId, businessDate }),
    queryFn: async () => {
      const response = await fetchDailyLogbookDetail(restaurantId, {
        business_date: businessDate,
      });
      return response?.data || null;
    },
    enabled: Boolean(open && restaurantId),
    staleTime: 10 * 60 * 1000,
  });

  useEffect(() => {
    if (!error) return;
    showNotification(error?.response?.data?.error || LOAD_ERROR_MESSAGE, "error");
  }, [error, showNotification]);

  useEffect(() => {
    setSelectedCenter(null);
    setExportOpen(false);
  }, [open, restaurantId, businessDate]);

  const handleCloseCenter = useCallback(() => setSelectedCenter(null), []);

  const restaurantName = detail?.restaurant?.name || restaurant?.name;

  const message = (() => {
    if (error) return LOAD_ERROR_MESSAGE;
    if (isFetching && !detail) return <Loader />;
    if (!detail) return NO_DETAILS_MESSAGE;
    return null;
  })();

  const handleExport = useCallback(
    async (format) => {
      const downloaded = await runExport({
        format,
        url: buildRestaurantExportUrl({ format, businessDate, restaurantId }),
        fallbackFilename: exportFilename({ format, businessDate, subject: restaurantName }),
      });

      if (downloaded) setExportOpen(false);
    },
    [runExport, businessDate, restaurantId, restaurantName],
  );

  const handleCloseExport = useCallback(() => setExportOpen(false), []);

  const exportAction = (
    <Button
      variant="outlined"
      size="small"
      startIcon={<FileDownloadOutlinedIcon />}
      onClick={() => setExportOpen(true)}
      disabled={Boolean(message) || exporting}
      sx={drillInExportButtonStyles}
      data-testid="daily-logbook-details-export"
    >
      Export
    </Button>
  );

  return (
    <React.Fragment>
      <DrillInDialog
        open={open}
        onClose={onClose}
        testId="daily-logbook-details-modal"
        closeTestId="daily-logbook-details-close"
        title={restaurantName}
        subtitle={businessDateLabel}
        message={message}
        actions={exportAction}
        left={
          <React.Fragment>
            <DailyLogbookMetricPanel
              title="Performance Snapshot"
              metrics={detail?.restaurant?.metrics || []}
              testIdPrefix="daily-logbook-details"
            />
            <RevenueCenterRail
              centers={detail?.revenue_centers || []}
              onSelect={setSelectedCenter}
              testIdPrefix="daily-logbook-details"
            />
          </React.Fragment>
        }
        right={
          <DailyLogbookSections
            sections={detail?.restaurant?.sections}
            testIdPrefix="daily-logbook-details"
          />
        }
      />

      <DailyLogbookExportDialog
        open={exportOpen}
        onClose={handleCloseExport}
        onConfirm={handleExport}
        loading={exporting}
        testId="daily-logbook-details-export-dialog"
        description={`Select the format you want to download. This report covers ${
          restaurantName || "this restaurant"
        }${businessDateLabel ? ` for ${businessDateLabel}` : ""}.`}
      />

      <RevenueCenterModal
        open={Boolean(selectedCenter)}
        center={selectedCenter}
        restaurantName={restaurantName}
        businessDateLabel={businessDateLabel}
        onClose={handleCloseCenter}
      />
    </React.Fragment>
  );
};
